import { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';

export const PANES_WIDTH = 360;
export const PANES_GAP = 8;

/**
 * Entrada y salida de los paneles derechos (DESIGN.md §2.3, informe apple-design propuesta 2): desde la derecha,
 * 8 px más opacidad, 0,2 s sin rebote; con «reducir movimiento», sin desplazamiento. Para que la salida se vea, quien
 * monta el panel lo pone dentro de <AnimatePresence> con una key fija.
 * - `contenedor` va en el propio elemento fijo (un padre con transform movería el `position: fixed` de lugar). Al
 *   entrar su fondo es opaco desde el primer cuadro: si se fundiera entero, se leería la lista de abajo mientras la
 *   columna aún no se corre (revisión de diseño del 17-10). Al salir sí se funde entero.
 * - `contenido` va en lo que está dentro del fondo: es lo que se funde al entrar.
 */
export function usePanelMotion() {
  const quieto = useReducedMotion();
  const transition = { type: 'spring' as const, bounce: 0, duration: 0.2 };
  return {
    contenedor: {
      initial: quieto ? false as const : { x: 8 },
      animate: { x: 0, opacity: 1 },
      exit: quieto ? { opacity: 0 } : { x: 8, opacity: 0 },
      transition,
    },
    contenido: { initial: { opacity: 0 }, animate: { opacity: 1 }, transition },
  };
}

/**
 * Columna derecha bajo la cabecera (85 px), sobre blanco, con las tarjetas apiladas a 8 px.
 * Desde lg empuja la conversación (Layout reserva PANES_WIDTH + 2 × PANES_GAP); bajo lg ocupa el ancho completo.
 */
export default function RightPanes({ children }: { children: ReactNode }) {
  const { contenedor, contenido } = usePanelMotion();
  return (
    <motion.div {...contenedor} className="fixed z-30 inset-x-0 bottom-0 top-[85px] lg:left-auto lg:w-[376px] bg-white p-2 flex flex-col">
      <motion.div {...contenido} className="flex-1 min-h-0 flex flex-col gap-2">
        {children}
      </motion.div>
    </motion.div>
  );
}
